import { FC, ReactElement, memo, useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import {
  ProxyServerConfigurationStatuses,
  ProxyServerHealthStatuses,
  ProxyServersQueryKeys,
  useCheckProxyServerAddressesMutation,
  useDeleteProxyServersMutation,
  useGetOneByIdProxyServersQuery,
  useUpdateProxyServerConf, useUpdateProxyServerHealth
} from '@/modules/ProxyServers';

import { ProxyServersOverviewView } from './ProxyServersOverviewView';

interface Props {
  proxyServerId: string;
}

export const ProxyServersOverviewContainer: FC<Props> = memo(({ proxyServerId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const {
    data: proxyServerData,
    error: proxyServerError,
    isLoading: isProxyServerLoading,
    isFetching: isProxyServerFetching,
    isError: isProxyServerError,
    isSuccess: isProxyServerRequestSuccess
  } = useGetOneByIdProxyServersQuery(proxyServerId)({
    enabled: !!proxyServerId
  });

  const { isLoading: isDeleteProxyServerLoading, mutate: deleteProxyServerMutation } = useDeleteProxyServersMutation(
    proxyServerId
  )({
    onSuccess: async () => {
      appMessageApi.success('Проксі-сервер успішно видалено!');

      await queryClient.refetchQueries([ProxyServersQueryKeys.GetAll]);

      navigate(AppRoutes.ProxyServers);
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  const {
    mutate: checkProxyServerAddressesMutation,
    isLoading: isCheckProxyServerAddressesLoading
  } = useCheckProxyServerAddressesMutation(proxyServerId)({
    onSuccess: () => {
      appMessageApi.success('Адреси перевіряються.');
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  useUpdateProxyServerHealth({ proxyServerId, isProxyServerRequestSuccess, queryClient });

  useUpdateProxyServerConf({ proxyServerId, isProxyServerRequestSuccess, queryClient });

  const handleDeleteProxyServer = useCallback((): void => {
    deleteProxyServerMutation();
  }, []);

  const handleCheckAddressesProxyServer = useCallback((): void => {
    checkProxyServerAddressesMutation();
  }, []);

  const handleEditProxyServer = useCallback((): void => {
    navigate(AppRoutes.ProxyServersByIdEdit.replace(':proxyServerId', proxyServerId));
  }, [proxyServerId]);

  const anyAddressInProccess = useMemo(() => {
    if (!proxyServerData) return false;

    return proxyServerData.addresses.some(
      (address) =>
        address.healthStatus === ProxyServerHealthStatuses.Checking ||
        address.configurationStatus === ProxyServerConfigurationStatuses.Checking ||
        address.configurationStatus === ProxyServerConfigurationStatuses.Configurating
    );
  }, [proxyServerData?.addresses]);

  return (
    <FallbackedLayout
      isLoading={ isProxyServerLoading }
      isError={ isProxyServerError }
      error={ proxyServerError }
      data={ proxyServerData }
    >
      { (proxyServer): ReactElement => (
        <ProxyServersOverviewView
          proxyServer={ proxyServer }
          deleteButtonLoading={ isDeleteProxyServerLoading }
          checkAddressesButtonLoading={ isCheckProxyServerAddressesLoading }
          disableButtons={
            isProxyServerFetching ||
            anyAddressInProccess ||
            isCheckProxyServerAddressesLoading ||
            isDeleteProxyServerLoading
          }
          handleCheckAddressesProxyServer={ handleCheckAddressesProxyServer }
          handleDeleteProxyServer={ handleDeleteProxyServer }
          handleEditProxyServer={ handleEditProxyServer }
        />
      ) }
    </FallbackedLayout>
  );
});
